import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import CTASection from '../components/CTASection';
import ChaosToOrder from '../components/ChaosToOrder';
import SEO from '../components/SEO';
import GradientOverlay from '../components/GradientOverlay';
import LogomarkPattern from '../components/LogomarkPattern';

const About = () => {
  const values = [
    {
      title: 'Clarity & Candour',
      description: 'We believe in straight talk and transparent processes. We demystify technology and provide clear, actionable roadmaps. No jargon, no hype—just honest guidance.'
    },
    {
      title: 'Pragmatic Innovation',
      description: 'We are not about chasing trends. We are about applying the right technology, at the right time, to solve the right problem. Our solutions are innovative yet practical, designed for real-world business impact.'
    },
    {
      title: 'Decisive Action',
      description: 'We believe in moving forward with purpose. We help our clients make informed decisions and then execute with precision and efficiency, delivering results, not just reports.'
    },
    {
      title: 'Enduring Partnership',
      description: 'Our goal is to build lasting relationships based on trust and mutual success. We empower our clients with the knowledge and systems they need to not just solve today\'s problems, but to own their future growth.'
    },
    {
      title: 'Excellence in Resolution',
      description: 'We are relentless in our pursuit of effective, elegant, and robust solutions. Our work isn\'t done until the problem is truly resolved.'
    }
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="About Us - Digital Transformation Experts"
        description="Learn about Resolv Digital, Gold Coast's trusted digital transformation consultancy. We deliver clear, decisive technology solutions that resolve your business challenges."
        keywords="About Resolv Digital, Digital Transformation Consultancy Gold Coast, CRM Experts Australia, Technology Consultants Brisbane, Business Solutions Queensland"
        canonical="https://resolvdigital.com.au/about"
      />
      
      {/* Hero Section - LIGHT */}
      <section style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#ffffff',
        padding: '120px clamp(20px, 5vw, 40px) 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Gradient Overlay */}
        <GradientOverlay variant="subtle" opacity={0.15} />
        
        {/* Logomark Pattern */}
        <LogomarkPattern opacity={0.03} density="sparse" tint="teal" />
        
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(13, 115, 119, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '180px',
          height: '180px',
          background: 'radial-gradient(circle, rgba(201, 162, 39, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />

        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(3rem, 6vw, 5rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #0D7377 50%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            About Resolv Digital
          </h1>
          <div style={{
            maxWidth: '900px'
          }}>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.5rem',
              lineHeight: 1.6,
              color: '#0a4447',
              marginBottom: '24px'
            }}>
              We don't sell you hype; we deliver resolution.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Story - LIGHT TEAL with Chaos Animation */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Chaos to Order Animation as Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        {/* Subtle gradient overlay to blend animation */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_e396212b-b701-4d20-8085-64e05af9a733/artifacts/g9nebm1y_post2_chaos_to_order.png"
                alt="From Chaos to Clarity"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '2px solid #0D7377',
                  boxShadow: '0 8px 32px rgba(13, 115, 119, 0.2)'
                }}
              />
            </div>
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                Our Story
              </h2>
              <div style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#0a4447'
              }}>
                <p style={{ marginBottom: '20px' }}>
                  After 18 years on the front lines of corporate digital transformation, I saw a frustrating paradox. 
                  The world's most powerful technologies were revolutionising large enterprises, yet the small and 
                  medium-sized businesses that are the lifeblood of our economy were being left behind—drowning in a 
                  sea of complexity, jargon, and prohibitive costs.
                </p>
                <p style={{ marginBottom: '20px' }}>
                  I saw brilliant entrepreneurs paralysed by choice, sold on hype, and ultimately left with shiny, 
                  expensive tools that didn't solve their core problems. My guiding principle has always been that 
                  technology is a means, not an end. It's a powerful tool, but only when wielded with a deep 
                  understanding of the business problem it's meant to solve.
                </p>
                <p style={{ marginBottom: '20px', fontWeight: 600, color: '#C9A227' }}>
                  That's why I founded Resolv Digital.
                </p>
                <p>
                  We are not another IT consultancy. We are a new kind of partner. We believe that digital 
                  transformation doesn't have to be a complex, costly, or never-ending journey. It can be a series of 
                  smart, incremental steps, each one focused on resolving a specific challenge or unlocking a clear 
                  opportunity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Logomark Pattern */}
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '40px'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #0D7377',
              borderRadius: '8px',
              padding: 'clamp(24px, 5vw, 48px)',
              boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0D7377',
                textTransform: 'uppercase',
                marginBottom: '24px',
                letterSpacing: '0.05em'
              }}>
                Our Mission
              </h3>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: '#0a4447'
              }}>
                To cut through the complexity and hype of digital transformation, delivering clear, decisive, and 
                impactful technology solutions that permanently resolve our clients' most pressing business challenges. 
                We don't just implement technology; we resolve problems, for good.
              </p>
            </div>

            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #C9A227',
              borderRadius: '8px',
              padding: 'clamp(24px, 5vw, 48px)',
              boxShadow: '0 8px 32px rgba(201, 162, 39, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#C9A227',
                textTransform: 'uppercase',
                marginBottom: '24px',
                letterSpacing: '0.05em'
              }}>
                Our Vision
              </h3>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: '#0a4447'
              }}>
                To be the most trusted partner for Australian SMEs and Scale-Ups seeking to thrive in the digital age, 
                known for our unwavering commitment to delivering practical, lasting solutions that make enterprise-level 
                capabilities accessible to all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values - LIGHT TEAL */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="gold" />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '80px',
            textAlign: 'center'
          }}>
            Our Core Values
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px'
          }}>
            {values.map((value, index) => (
              <div key={index} style={{
                backgroundColor: '#ffffff',
                border: '1px solid rgba(13, 115, 119, 0.2)',
                borderRadius: '8px',
                padding: 'clamp(24px, 5vw, 40px)',
                display: 'flex',
                gap: '20px',
                alignItems: 'flex-start',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#0D7377';
                e.currentTarget.style.transform = 'translateX(8px)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(13, 115, 119, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(13, 115, 119, 0.2)';
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.06)';
              }}
              >
                <CheckCircle2 size={32} style={{
                  color: '#0D7377',
                  flexShrink: 0,
                  marginTop: '4px'
                }} />
                <div>
                  <h3 style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    color: '#0a4447',
                    marginBottom: '12px'
                  }}>
                    {value.title}
                  </h3>
                  <p style={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1.125rem',
                    lineHeight: 1.7,
                    color: '#4a5568'
                  }}>
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Elements - DARK (contrast section) */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#084144',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.1} />
        </div>
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#C9A227',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Our Brand
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div style={{
              textAlign: 'center'
            }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-transform-45/artifacts/zhvprbqi_logomark_web_large_1024x1024.png"
                alt="Resolv Digital Logomark"
                style={{
                  width: '200px',
                  height: 'auto',
                  margin: '0 auto',
                  filter: 'drop-shadow(0 0 40px rgba(201, 162, 39, 0.3))'
                }}
              />
            </div>

            <div>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#C9A227',
                marginBottom: '24px',
                textTransform: 'uppercase'
              }}>
                Our Promise
              </h3>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#ffffff',
                marginBottom: '20px'
              }}>
                At Resolv Digital, our work is about more than just technology. It's about applying decades of 
                experience in business analysis, enterprise architecture, and change management to your unique situation.
              </p>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#ffffff',
                marginBottom: '20px'
              }}>
                We bring the strategic thinking of the enterprise world and tailor it for the agility and ambition 
                of SMEs and scale-ups.
              </p>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.25rem',
                lineHeight: 1.8,
                color: '#C9A227',
                fontWeight: 600
              }}>
                We will help you identify the right problem, implement the right solution, and resolve it. Once, and for good.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default About;
